import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  Paper,
  Stack,
  Button,
} from "@mui/material";
import { ExpandMore, EditOutlined, DeleteOutline } from "@mui/icons-material";
import { mapInsuranceData } from "../../helpers/map-insurance-details";
import CreateInsurance from "../createInsurance";
import { CreateInsuranceFields, InsuranceResponse } from "../../types";

const capitalize = (value: string | number) => {
  if (typeof value !== "string") return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

interface InsuranceAccordionProps {
  insurance: InsuranceResponse;
  isPermitted: boolean;
  index: number;
  expandedAccordions: number[];
  hasNewInsurance: boolean;
  toggleAccordion: (id: number) => void;
  editId: number | null;
  handleEdit: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number,
  ) => void;
  setRemoveDialogOpen: (id: number) => void;
  editData: Partial<CreateInsuranceFields> | null;
  updateFields: (fields: Partial<CreateInsuranceFields>) => void;
  setEditId: (id: number | null) => void;
  setEditData: (data: Partial<CreateInsuranceFields>) => void;
  handleSave: () => void;
  handleCancel: () => void;
}

const InsuranceAccordion: React.FC<InsuranceAccordionProps> = ({
  insurance,
  index,
  expandedAccordions,
  toggleAccordion,
  editId,
  handleEdit,
  setRemoveDialogOpen,
  hasNewInsurance,
  editData,
  updateFields,
  handleSave,
  handleCancel,
  isPermitted = true,
}) => {
  return (
    <Accordion
      key={insurance.id}
      expanded={expandedAccordions.includes(insurance.id)}
      onChange={() => toggleAccordion(insurance.id)}
      sx={{
        boxShadow: "none",
        p: 1,
        backgroundColor: "rgba(250, 250, 250, 1)",
      }}
      className="scInsurance"
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ width: "calc(100% - 50px)" }}>
          <Typography sx={{ fontSize: "16px" }}>
            {`#${index + 1} Insurance`}
          </Typography>
        </Box>
        {isPermitted && (
          <>
            {editId !== insurance.id && !hasNewInsurance && (
              <IconButton onClick={(e) => handleEdit(e, insurance.id)}>
                <EditOutlined />
              </IconButton>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setRemoveDialogOpen(insurance.id);
              }}
            >
              <DeleteOutline />
            </IconButton>
          </>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={1}>
          {insurance.id !== editId ? (
            Object.entries(mapInsuranceData(insurance)).map(([key, value]) => (
              <Paper key={key} sx={{ p: "8px 16px", display: "flex" }}>
                <Box sx={{ width: "30%" }}>
                  <Typography>{key}</Typography>
                </Box>
                <Box sx={{ width: "70%" }}>
                  <Typography color="rgba(0, 0, 0, 0.6)">
                    {capitalize(value as string | number)}
                  </Typography>
                </Box>
              </Paper>
            ))
          ) : (
            <>
              <CreateInsurance fields={editData} updateFields={updateFields} />
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => handleCancel()}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleSave()}
                >
                  {typeof editId !== "number" ? "Create Insurance" : "Save"}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default InsuranceAccordion;
